import { MenuItem, Radio, Text, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import FeeTable, { FeeTableGetFeeEditPathFn } from "features/fees/FeeTable";
import { FundFamilySetupTabProps } from "features/fund-family/setup/FundFamilySetupTabProps";
import {
  getFundFamilyConfigRetrieveQueryKey,
  useFundFamilyConfigRetrieveSuspense,
  useFundFamilyConfigUpdate,
} from "gen/endpoints/fund-family-config/fund-family-config";
import { FeeInterestMethodologyEnum } from "gen/model/feeInterestMethodologyEnum";
import { FundFamilyConfigReadData } from "gen/model/fundFamilyConfigReadData";
import { FundFamilyConfigUpsertDataRequest } from "gen/model/fundFamilyConfigUpsertDataRequest";
import { InterestDayCountEnum } from "gen/model/interestDayCountEnum";
import { ManagementFeeCallMethodologyEnum } from "gen/model/managementFeeCallMethodologyEnum";
import { ManagementFeeDistributionMethodologyEnum } from "gen/model/managementFeeDistributionMethodologyEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { fundFamilyConfigUpdateBody } from "gen/schemas/fund-family-config/fund-family-config";
import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { FormRadioGroup } from "shared/components/radio/FormRadio";
import { rootRoute } from "shared/navigation/Routes";
import { formatPercentage } from "shared/utils/formattingUtils";

export const FundFamilySetupFundTabFees: React.FC = () => {
  const { fundFamilyId } = useOutletContext<FundFamilySetupTabProps>();

  const baseRoute = useMemo(
    () =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fund({})
        .fees({}),
    [fundFamilyId],
  );

  const getFeeEditPath = useCallback<FeeTableGetFeeEditPathFn>(
    (feeId) => baseRoute.view({ feeId }).$,
    [baseRoute],
  );

  return (
    <PageTabBody>
      <FeeTable
        fundFamilyId={fundFamilyId}
        retrieveParams={{}}
        getFeeEditPath={getFeeEditPath}
        showTimeline={true}
      />
      <ContentBlockGroup>
        <FundFamilyFeeConfigBlock fundFamilyId={fundFamilyId} />
        <FundFamilyFeeInterestConfigBlock fundFamilyId={fundFamilyId} />
      </ContentBlockGroup>
    </PageTabBody>
  );
};

const FundFamilyFeeConfigBlock: React.FC<{
  fundFamilyId: string;
}> = ({ fundFamilyId }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.managementFeeConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>

        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeConfig.capital_call.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeConfig.capital_call.${fundFamilyConfig.management_fee_call_methodology}.title`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeConfig.distribution.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${fundFamilyConfig.management_fee_distribution_methodology}.title`,
              )}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <ManagementFeeEditDrawer
          fundFamilyId={fundFamilyId}
          fundFamilyConfig={fundFamilyConfig}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

const FundFamilyFeeInterestConfigBlock: React.FC<{
  fundFamilyId: string;
}> = ({ fundFamilyId }) => {
  const { t } = useTranslation(["fundFamily", "common"]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  return (
    <>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>
            {t("fundFamily:config.managementFeeInterestConfig.title")}
          </ContentBlockTitle>
          <Restricted permission={PermissionTypeEnum.config}>
            <ContentBlockActions>
              <ContentBlockMenu>
                <MenuItem onClick={onOpen}>{t("common:edit")}</MenuItem>
              </ContentBlockMenu>
            </ContentBlockActions>
          </Restricted>
        </ContentBlockHeader>

        <ContentBlockBody variant="descriptions">
          <Descriptions>
            <Descriptions.Item
              label={t(
                "fundFamily:setup.profile.advanced.feeAccrualInterestRate",
              )}
            >
              {formatPercentage(
                new Decimal(fundFamilyConfig.fee_accrual_interest_rate).times(
                  100,
                ),
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeInterestConfig.interest_methodology.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${fundFamilyConfig.fee_interest_methodology}.title`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                "fundFamily:config.managementFeeInterestConfig.day_count.title",
              )}
            >
              {t(
                `fundFamily:config.managementFeeInterestConfig.day_count.${fundFamilyConfig.management_fee_interest_day_count}`,
              )}
            </Descriptions.Item>
          </Descriptions>
        </ContentBlockBody>
      </ContentBlock>
      {isOpen && (
        <ManagementFeeInterestEditDrawer
          fundFamilyId={fundFamilyId}
          fundFamilyConfig={fundFamilyConfig}
          onCloseComplete={onClose}
        />
      )}
    </>
  );
};

interface ManagementFeeInterestEditDrawerProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
  fundFamilyConfig: FundFamilyConfigReadData;
}

export const ManagementFeeInterestEditDrawer: React.FC<
  ManagementFeeInterestEditDrawerProps
> = ({ fundFamilyId, onCloseComplete, fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily", "enums"]);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();
  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    resolver: zodResolver(
      fundFamilyConfigUpdateBody.pick({
        fee_interest_methodology: true,
      }),
    ),
    defaultValues: fundFamilyConfig,
  });

  const onSubmit = async (data: FundFamilyConfigUpsertDataRequest) =>
    await updateFundFamilyConfig({
      fundFamilyId,
      data,
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    });

  return (
    <FormDrawer
      title={t("fundFamily:config.managementFeeInterestConfig.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t(
          "fundFamily:config.managementFeeInterestConfig.edit.edited",
        ),
      }}
      onCloseComplete={onCloseComplete}
    >
      <FormNumberInput
        name="fee_accrual_interest_rate"
        valueType="percent"
        title={t("fundFamily:setup.profile.advanced.feeAccrualInterestRate")}
      />
      <FormSelect
        name="management_fee_interest_day_count"
        title={t(
          "fundFamily:config.managementFeeInterestConfig.day_count.title",
        )}
      >
        {Object.values(InterestDayCountEnum).map((value) => (
          <SelectOption key={value} value={value}>
            {t(`enums:InterestDayCountEnum.${value}`)}
          </SelectOption>
        ))}
      </FormSelect>
      <Text fontSize="16px" fontWeight="semiBold" pb="12px">
        {t(
          "fundFamily:config.managementFeeInterestConfig.interest_methodology.title",
        )}
        :
      </Text>
      <FormRadioGroup variant="card" name="fee_interest_methodology">
        {Object.keys(FeeInterestMethodologyEnum).map((interest_methodology) => (
          <Radio value={interest_methodology} key={interest_methodology}>
            <Text>
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${
                  interest_methodology as "irr"
                }.title`,
              )}
            </Text>
            <Text>
              {t(
                `fundFamily:config.managementFeeInterestConfig.interest_methodology.${
                  interest_methodology as "irr"
                }.description`,
              )}
            </Text>
          </Radio>
        ))}
      </FormRadioGroup>
    </FormDrawer>
  );
};

interface ManagementFeeEditDrawerProps {
  fundFamilyId: string;
  onCloseComplete: () => void;
  fundFamilyConfig: FundFamilyConfigReadData;
}

export const ManagementFeeEditDrawer: React.FC<
  ManagementFeeEditDrawerProps
> = ({ fundFamilyId, onCloseComplete, fundFamilyConfig }) => {
  const { t } = useTranslation(["fundFamily"]);
  const queryClient = useQueryClient();

  const { mutateAsync: updateFundFamilyConfig } = useFundFamilyConfigUpdate();
  const form = useForm<FundFamilyConfigUpsertDataRequest>({
    resolver: zodResolver(
      fundFamilyConfigUpdateBody.pick({
        management_fee_call_methodology: true,
        management_fee_distribution_methodology: true,
      }),
    ),
    defaultValues: fundFamilyConfig,
  });

  const onSubmit = async (data: FundFamilyConfigUpsertDataRequest) =>
    await updateFundFamilyConfig({
      fundFamilyId,
      data,
    });

  const onSuccess = async () =>
    await queryClient.invalidateQueries({
      queryKey: getFundFamilyConfigRetrieveQueryKey(fundFamilyId),
    });

  const distributionMethodologyChoices = Object.keys(
    ManagementFeeDistributionMethodologyEnum,
  );

  return (
    <FormDrawer
      title={t("fundFamily:config.managementFeeConfig.edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t("fundFamily:config.managementFeeConfig.edit.edited"),
      }}
      onCloseComplete={onCloseComplete}
    >
      <Text fontSize="16px" fontWeight="semiBold" pb="12px">
        {t("fundFamily:config.managementFeeConfig.capital_call.title")}:
      </Text>
      <FormRadioGroup variant="card" name="management_fee_call_methodology">
        {Object.keys(ManagementFeeCallMethodologyEnum).map(
          (call_methodology) => (
            <Radio value={call_methodology} key={call_methodology}>
              <Text>
                {t(
                  `fundFamily:config.managementFeeConfig.capital_call.${
                    call_methodology as "net_fees"
                  }.title`,
                )}
              </Text>
              <Text>
                {t(
                  `fundFamily:config.managementFeeConfig.capital_call.${
                    call_methodology as "net_fees"
                  }.description`,
                )}
              </Text>
            </Radio>
          ),
        )}
      </FormRadioGroup>
      <Text fontSize="16px" fontWeight="semiBold" mb="12px" mt="16px">
        {t("fundFamily:config.managementFeeConfig.distribution.title")}:
      </Text>
      <FormRadioGroup
        variant="card"
        name="management_fee_distribution_methodology"
      >
        {distributionMethodologyChoices.map((distribution_methodology) => (
          <Radio
            value={distribution_methodology}
            key={distribution_methodology}
          >
            <Text>
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${
                  distribution_methodology as "fee_holdback"
                }.title`,
              )}
            </Text>
            <Text>
              {t(
                `fundFamily:config.managementFeeConfig.distribution.${
                  distribution_methodology as "fee_holdback"
                }.description`,
              )}
            </Text>
          </Radio>
        ))}
      </FormRadioGroup>
    </FormDrawer>
  );
};
